import { HttpClient } from './services';
import { isDevelopment } from '@constants/settings';
import {
    AirflowAssetEventList,
    AirflowCalendarResponse,
    AirflowDag,
    AirflowDagDetails,
    AirflowDagList,
    AirflowDagRun,
    AirflowDagRunList,
    AirflowDagSource,
    AirflowImportErrorList,
    AirflowTagList,
    AirflowTaskDefinition,
    AirflowTaskDefinitionList,
    AirflowTaskInstance,
    AirflowTaskInstanceLinks,
    AirflowTaskInstanceList,
    AirflowTaskLogsResponse,
    AirflowXcomEntry,
    AirflowXcomEntryList,
} from '@pages/airflow-beta/types';

type EnvVariables = Record<string, any>;

/*
 * These calls hit Apache Airflow's own webserver directly (its native REST API v1, per
 * https://airflow.apache.org/docs/apache-airflow/2.6.0/stable-rest-api-ref.html) — NOT the
 * platform gateway. Same host the old iframe page embedded (src/pages/airflow's original
 * implementation used this exact isDevelopment/REACT_APP_AIRFLOW_URL fallback pattern).
 * Confirmed working for listDags (2026-08-12) — see the hybrid toggle in
 * airflow-dashboard-handler.ts for which other methods have been verified since.
 */
function airflowHost(Env: EnvVariables) {
    return isDevelopment ? 'https://airflow.3535353535.deepiq.com' : Env?.REACT_APP_AIRFLOW_URL?.replace(/\/home$/, '');
}

function readBase(Env: EnvVariables) {
    return `${airflowHost(Env)}/api/v1`;
}

/*
 * Appends a query param, expanding arrays as repeated keys and skipping empty values
 */
function appendParam(searchParams: URLSearchParams, key: string, value: unknown) {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, String(item)));
        return;
    }
    searchParams.set(key, String(value));
}

/*
 * Builds a query string from a params object, skipping empty/undefined values
 */
function buildQuery(params: Record<string, unknown>) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => appendParam(searchParams, key, value));
    const query = searchParams.toString();
    return query ? `?${query}` : '';
}

/*
 * Performs a GET request via HttpClient's promise API and unwraps the response body
 */
async function get<T>(url: string): Promise<T> {
    const response = await HttpClient.GetPromise(url);
    return response.data;
}

/*
 * Performs a POST request via HttpClient's promise API and unwraps the response body
 */
async function post<T>(url: string, data: Record<string, unknown> = {}): Promise<T> {
    const response = await HttpClient.PostPromise(url, data);
    return response.data;
}

/*
 * Performs a PATCH request via HttpClient's promise API and unwraps the response body
 */
async function patch<T>(url: string, data: Record<string, unknown> = {}): Promise<T> {
    const response = await HttpClient.PatchPromise(url, data);
    return response.data;
}

/*
 * Performs a DELETE request via HttpClient's promise API and unwraps the response body
 */
async function del<T>(url: string): Promise<T> {
    const response = await HttpClient.DeletePromise(url);
    return response.data;
}

type ListDagsParams = {
    limit?: number;
    offset?: number;
    orderBy?: string;
    dagIdPattern?: string;
    paused?: boolean;
    onlyActive?: boolean;
    tags?: string[];
};

type ListDagRunsParams = {
    limit?: number;
    offset?: number;
    order_by?: string;
    state?: string[];
    runType?: string[];
    runAfterGte?: string;
    runAfterLte?: string;
    logicalDateGte?: string;
    logicalDateLte?: string;
    startDateGte?: string;
    startDateLte?: string;
    endDateGte?: string;
    endDateLte?: string;
    durationGte?: number;
    durationLte?: number;
    runIdPattern?: string;
    triggeringUserNamePattern?: string;
};

type ListDagTaskInstancesParams = {
    limit?: number;
    offset?: number;
    order_by?: string;
    state?: string[];
    runAfterGte?: string;
    runAfterLte?: string;
    taskId?: string;
};

type ListRunTaskInstancesParams = {
    limit?: number;
    offset?: number;
    order_by?: string;
    state?: string[];
};

/*
 * Sorts by an `order_by`-style field string (optional leading "-" for descending). Used for
 * endpoints like GET .../taskInstances that have no server-side `order_by` support at all in
 * 2.6.0 — see API_GAPS.md — so the requested sort is applied client-side on the fetched page.
 */
function sortByOrderBy<T>(items: T[], orderBy: string | undefined, getValue: (item: T, field: string) => any): T[] {
    if (!orderBy) return items;
    const desc = orderBy.startsWith('-');
    const field = desc ? orderBy.slice(1) : orderBy;
    const sorted = [...items].sort((a, b) => {
        const av = getValue(a, field);
        const bv = getValue(b, field);
        if (av === bv) return 0;
        return av > bv ? 1 : -1;
    });
    return desc ? sorted.reverse() : sorted;
}

/*
 * Real API client for V1's ported Airflow Dashboard, calling Apache Airflow's own stable
 * REST API v1 through the platform gateway. Only listDags is confirmed against the live
 * backend so far — everything else is pending confirmation, endpoint by endpoint.
 */
export const AirflowDashboardHandler = {
    /*
     * Lists DAGs with search/paused/tag filters, sort, and pagination — matches Airflow's
     * native GET /dags exactly (see stable-rest-api-ref.html). There is no "last dag run
     * state" filter on this endpoint — see API_GAPS.md for why that filter was removed from
     * the UI instead of being faked client-side.
     */
    async listDags(Env: EnvVariables, params: ListDagsParams = {}): Promise<AirflowDagList> {
        const query = buildQuery({
            limit: params.limit,
            offset: params.offset,
            order_by: params.orderBy,
            dag_id_pattern: params.dagIdPattern,
            paused: params.paused,
            only_active: params.onlyActive,
            tags: params.tags,
        });
        return get(`${readBase(Env)}/dags${query}`);
    },

    /*
     * Fetches a single DAG's summary fields
     */
    async getDag(Env: EnvVariables, dagId: string): Promise<AirflowDag> {
        return get(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}`);
    },

    /*
     * Fetches a DAG's extended parse/config details
     */
    async getDagDetails(Env: EnvVariables, dagId: string): Promise<AirflowDagDetails> {
        return get(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/details`);
    },

    /*
     * Deletes a DAG. Goes through Airflow's own host directly (readBase), not the platform
     * gateway's legacy /airflow/api proxy (DataSourceHandler.DeleteDagAirflow) — that route
     * 400s for reasons opaque to this codebase (it's a separate gateway service, not Airflow's
     * webserver, so none of the OpenAPI-spec-based debugging here applies to it). This method
     * hits the same well-understood host as every other confirmed-working call.
     */
    async deleteDag(Env: EnvVariables, dagId: string): Promise<void> {
        return del(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}`);
    },

    /*
     * Updates DAG fields, most commonly the paused state
     */
    async patchDag(Env: EnvVariables, dagId: string, body: Partial<Pick<AirflowDag, 'is_paused'>>): Promise<AirflowDag> {
        return patch(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}`, body);
    },

    /*
     * Triggers a new run of a DAG
     */
    async triggerDag(
        Env: EnvVariables,
        dagId: string,
        params: {
            logical_date?: string | null;
            conf?: Record<string, unknown>;
            run_id?: string;
            note?: string;
        } = {}
    ): Promise<AirflowDagRun> {
        // data_interval_start/data_interval_end are dump_only in Airflow's DAGRunSchema
        // (readOnly in the OpenAPI spec too) — sending them at all, even as null, makes
        // marshmallow's default RAISE-on-unknown-field behavior reject the whole request
        // with a 400. They must be omitted from the POST body entirely.
        const body: Record<string, unknown> = {
            conf: params.conf ?? {},
            logical_date: params.logical_date ?? null,
        };
        if (params.run_id) body.run_id = params.run_id;
        if (params.note) body.note = params.note;
        return post(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns`, body);
    },

    /*
     * Fetches a single task definition of a DAG
     */
    async getDagTask(Env: EnvVariables, dagId: string, taskId: string): Promise<AirflowTaskDefinition> {
        return get(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/tasks/${encodeURIComponent(taskId)}`);
    },

    /*
     * Lists every task definition of a DAG
     */
    async listDagTasks(Env: EnvVariables, dagId: string, params: { orderBy?: string } = {}): Promise<AirflowTaskDefinitionList> {
        const query = buildQuery({ order_by: params.orderBy });
        return get(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/tasks${query}`);
    },

    /*
     * Lists distinct DAG tags for the tag filter dropdown. Airflow 2.6.0 has no standalone
     * /dagTags endpoint (confirmed against its 2.6.0 OpenAPI spec — another V2-inherited
     * assumption of a newer Airflow version) — tags only exist as a field on each DAG object,
     * so this paginates GET /dags and aggregates the distinct tag names client-side.
     */
    async listDagTags(
        Env: EnvVariables,
        params: { limit?: number; offset?: number; orderBy?: string; tagNamePrefixPattern?: string } = {}
    ): Promise<AirflowTagList> {
        const tagNames = new Set<string>();
        const pageSize = 100;
        const maxPages = 20;
        let offset = 0;
        for (let page = 0; page < maxPages; page += 1) {
            const response: AirflowDagList = await get(`${readBase(Env)}/dags${buildQuery({ limit: pageSize, offset, only_active: false })}`);
            response.dags.forEach((dag) => dag.tags.forEach((tag) => tagNames.add(tag.name)));
            offset += pageSize;
            if (offset >= (response.total_entries ?? 0)) break;
        }
        let tags = Array.from(tagNames);
        if (params.tagNamePrefixPattern) tags = tags.filter((name) => name.startsWith(params.tagNamePrefixPattern as string));
        tags.sort();
        if (params.orderBy?.startsWith('-')) tags.reverse();
        const offsetParam = params.offset ?? 0;
        const limitedTags = params.limit ? tags.slice(offsetParam, offsetParam + params.limit) : tags.slice(offsetParam);
        return { tags: limitedTags, total_entries: tags.length };
    },

    /*
     * Lists DAG import/parse errors — matches Airflow's native GET /importErrors, surfaced as
     * the "DAG Import Errors" banner at the top of the DAGs list in the real Airflow UI
     */
    async listImportErrors(Env: EnvVariables, params: { limit?: number; offset?: number; orderBy?: string } = {}): Promise<AirflowImportErrorList> {
        const query = buildQuery({ limit: params.limit, offset: params.offset, order_by: params.orderBy });
        return get(`${readBase(Env)}/importErrors${query}`);
    },

    /*
     * Fetches a DAG's source code by its file_token — Airflow 2.6.0 has no DAG-versioning
     * REST concept, so there's exactly one current source per DAG (see the DAG object's
     * own file_token field, not a separate version-listing endpoint). This endpoint does
     * content negotiation (per the OpenAPI spec it can reply with either
     * `application/json` -> {content: "..."} or `plain/text` -> a raw string depending on
     * the Accept header) — our HttpClient doesn't let us force Accept: application/json, so
     * whichever shape comes back is normalized here instead of assuming the JSON wrapper
     * (that assumption silently rendered an empty code viewer whenever a raw string came back).
     */
    async getDagSource(Env: EnvVariables, fileToken: string): Promise<AirflowDagSource> {
        const response = await get<AirflowDagSource | string>(`${readBase(Env)}/dagSources/${encodeURIComponent(fileToken)}`);
        return typeof response === 'string' ? { content: response, dag_id: fileToken } : response;
    },

    /*
     * Pushes edited DAG source to the platform gateway's DAG repo sync — unlike every other
     * method in this file, this does NOT hit Airflow's own webserver (Airflow's REST API v1
     * has no write endpoint for DAG source at all, since source is a file in a git-backed repo,
     * not a DB row). Confirmed BE contract (2026-09-02): POST {gatewayUrl}/airflow/api/v1/
     * dags/push, body { fileName, dagContent, overwriteIfExists }. `fileName` is the DAG's own
     * dagId, matching how every other read in this file identifies a DAG. `overwriteIfExists`
     * is always true here since the Code tab only ever edits an already-existing DAG's source.
     */
    async pushDagSource(Env: EnvVariables, fileName: string, dagContent: string): Promise<unknown> {
        return post(`${Env?.REACT_APP_PLATFORM_URL}/airflow/api/v1/dags/push`, { fileName, dagContent, overwriteIfExists: true });
    },

    /*
     * Lists DAG runs for a single DAG with the full filter/sort/pagination set
     */
    async listDagRuns(Env: EnvVariables, dagId: string, params: ListDagRunsParams = {}): Promise<AirflowDagRunList> {
        // Confirmed 2.6.0 date filters are execution_date/start_date/end_date/updated_at only
        // (no separate "logical_date" or "run_after" fields, both Airflow 3.x-era terms) — the
        // UI's Logical Date and Run After filters both collapse onto execution_date here.
        // run_type/duration/run_id_pattern/triggering_user_name_pattern aren't confirmed for
        // 2.6.0 either, but unlike order_by (enum-validated) unknown query params don't 400 —
        // they're sent on the chance the server supports them, worst case silently no-op.
        const query = buildQuery({
            limit: params.limit,
            offset: params.offset,
            order_by: params.order_by,
            state: params.state,
            run_type: params.runType,
            execution_date_gte: params.runAfterGte ?? params.logicalDateGte,
            execution_date_lte: params.runAfterLte ?? params.logicalDateLte,
            start_date_gte: params.startDateGte,
            start_date_lte: params.startDateLte,
            end_date_gte: params.endDateGte,
            end_date_lte: params.endDateLte,
            duration_gte: params.durationGte,
            duration_lte: params.durationLte,
            run_id_pattern: params.runIdPattern,
            triggering_user_name_pattern: params.triggeringUserNamePattern,
        });
        return get(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns${query}`);
    },

    /*
     * Updates a DAG run's state or note (used for mark-success/mark-failed)
     */
    async patchDagRun(Env: EnvVariables, dagId: string, dagRunId: string, body: { state?: string; note?: string | null }): Promise<AirflowDagRun> {
        return patch(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}`, body);
    },

    /*
     * Deletes a DAG run
     */
    async deleteDagRun(Env: EnvVariables, dagId: string, dagRunId: string): Promise<void> {
        return del(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}`);
    },

    /*
     * Fetches a single DAG run
     */
    async getDagRun(Env: EnvVariables, dagId: string, dagRunId: string): Promise<AirflowDagRun> {
        return get(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}`);
    },

    /*
     * Builds calendar heatmap entries by re-purposing the dagRuns list endpoint (Airflow has
     * no dedicated calendar endpoint) — capped at 1000 runs per the visible month window
     */
    async getDagCalendar(
        Env: EnvVariables,
        dagId: string,
        params: { logicalDateGte?: string; logicalDateLte?: string }
    ): Promise<AirflowCalendarResponse> {
        const query = buildQuery({
            limit: 1000,
            order_by: 'execution_date',
            execution_date_gte: params.logicalDateGte,
            execution_date_lte: params.logicalDateLte,
        });
        const response = await get<AirflowDagRunList>(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns${query}`);
        const dagRuns = response.dag_runs.map((run) => ({
            count: 1,
            date: run.execution_date ?? run.logical_date ?? '',
            state: run.state,
        }));
        return { dag_runs: dagRuns, total_entries: response.total_entries ?? dagRuns.length };
    },

    /*
     * Dry-runs or executes clearing a DAG run's task instances
     */
    async clearDagRun(
        Env: EnvVariables,
        dagId: string,
        dagRunId: string,
        payload: { dryRun: boolean; onlyFailed?: boolean; onlyNew?: boolean; runOnLatestVersion?: boolean }
    ): Promise<{ task_instances: AirflowTaskInstance[] }> {
        return post(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/clear`, {
            dry_run: payload.dryRun,
            only_failed: payload.onlyFailed,
            only_new: payload.onlyNew,
            run_on_latest_version: payload.runOnLatestVersion,
        });
    },

    /*
     * Lists task instances of a DAG across all its runs. order_by and task_id are NOT sent
     * on the wire — this endpoint's parameter list in the 2.6.0 spec is only
     * PageLimit/PageOffset plus the date/state/pool/queue filters; neither order_by nor a
     * task_id filter exists (task_id is only a path param on the single-task-instance
     * endpoint), and sending either caused a 400.
     */
    async listDagTaskInstances(Env: EnvVariables, dagId: string, params: ListDagTaskInstancesParams = {}): Promise<AirflowTaskInstanceList> {
        // Same execution_date-not-run_after mismatch as listDagRuns — task instances are
        // filtered by their parent run's execution_date in 2.6.0, not run_after/logical_date.
        const filters = {
            state: params.state,
            execution_date_gte: params.runAfterGte,
            execution_date_lte: params.runAfterLte,
        };
        if (!params.taskId) {
            const query = buildQuery({ limit: params.limit, offset: params.offset, ...filters });
            const response = await get<AirflowTaskInstanceList>(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/~/taskInstances${query}`);
            return { ...response, task_instances: sortByOrderBy(response.task_instances, params.order_by, (item, field) => (item as any)[field] ?? '') };
        }
        // task_id has no server-side filter, and this is a genuinely paginated tab (offset
        // Prev/Next) — filtering only within one fetched page would silently corrupt
        // pagination (most of a page could belong to other tasks). Pages through instead
        // (same 20-page-of-100 cap as the listDags/listDagTags workarounds), filters
        // client-side on task_id, then slices the requested offset/limit out of the
        // filtered set so pagination totals stay accurate.
        const pageSize = 100;
        const maxPages = 20;
        const matched: AirflowTaskInstance[] = [];
        let offset = 0;
        for (let page = 0; page < maxPages; page += 1) {
            const query = buildQuery({ limit: pageSize, offset, ...filters });
            // eslint-disable-next-line no-await-in-loop
            const response: AirflowTaskInstanceList = await get(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/~/taskInstances${query}`);
            matched.push(...response.task_instances.filter((ti) => ti.task_id === params.taskId));
            offset += pageSize;
            if (offset >= (response.total_entries ?? 0)) break;
        }
        const sorted = sortByOrderBy(matched, params.order_by, (item, field) => (item as any)[field] ?? '');
        const requestedOffset = params.offset ?? 0;
        const page = params.limit ? sorted.slice(requestedOffset, requestedOffset + params.limit) : sorted.slice(requestedOffset);
        return { task_instances: page, total_entries: sorted.length };
    },

    /*
     * Lists task instances belonging to a single DAG run
     */
    async listRunTaskInstances(
        Env: EnvVariables,
        dagId: string,
        dagRunId: string,
        params: ListRunTaskInstancesParams = {}
    ): Promise<AirflowTaskInstanceList> {
        // order_by not sent — same "no order_by param on this endpoint at all" gap as
        // listDagTaskInstances (it's the same underlying GET .../taskInstances endpoint,
        // just with a concrete dag_run_id instead of "~"). Sorted client-side instead.
        const query = buildQuery({ limit: params.limit, offset: params.offset, state: params.state });
        const response = await get<AirflowTaskInstanceList>(
            `${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances${query}`
        );
        return { ...response, task_instances: sortByOrderBy(response.task_instances, params.order_by, (item, field) => (item as any)[field] ?? '') };
    },

    /*
     * Dry-runs or executes clearing specific task instances of a DAG run
     */
    async clearTaskInstances(
        Env: EnvVariables,
        dagId: string,
        payload: {
            dryRun: boolean;
            dagRunId: string;
            taskIds: string[];
            includePast?: boolean;
            includeFuture?: boolean;
            includeUpstream?: boolean;
            includeDownstream?: boolean;
            onlyFailed?: boolean;
        }
    ): Promise<{ task_instances: AirflowTaskInstance[] }> {
        return post(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/clearTaskInstances`, {
            dry_run: payload.dryRun,
            dag_run_id: payload.dagRunId,
            task_ids: payload.taskIds,
            include_past: payload.includePast,
            include_future: payload.includeFuture,
            include_upstream: payload.includeUpstream,
            include_downstream: payload.includeDownstream,
            only_failed: payload.onlyFailed,
        });
    },

    /*
     * Fetches a task instance's log content for a given try number, normalizing the
     * gateway's response shape (bare string vs. an object with a content field)
     */
    async getTaskLogs(
        Env: EnvVariables,
        dagId: string,
        dagRunId: string,
        taskId: string,
        tryNumber = 1,
        mapIndex = -1
    ): Promise<AirflowTaskLogsResponse> {
        const query = buildQuery({ map_index: mapIndex });
        const url = `${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances/${encodeURIComponent(taskId)}/logs/${tryNumber}${query}`;
        const result = await get<unknown>(url);
        if (typeof result === 'string') {
            return { content: result, continuation_token: null };
        }
        if (result && typeof result === 'object' && 'content' in (result as Record<string, unknown>)) {
            return result as AirflowTaskLogsResponse;
        }
        throw new Error('Unexpected response shape from task logs endpoint');
    },

    /*
     * Fetches a single task instance, scoped to a run and optional map index
     */
    async getRunTaskInstance(Env: EnvVariables, dagId: string, dagRunId: string, taskId: string, mapIndex = -1): Promise<AirflowTaskInstance> {
        const query = mapIndex >= 0 ? buildQuery({ map_index: mapIndex }) : '';
        return get(`${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances/${encodeURIComponent(taskId)}${query}`);
    },

    /*
     * Fetches operator-provided extra links for a task instance — confirmed to live under
     * v1 in Airflow 2.6.0 (v2 base isn't needed here, unlike newer Airflow versions). The
     * OpenAPI spec documents an "ExtraLinkCollection" wrapper ({ extra_links: [...] }), but
     * the real endpoint (airflow/api_connexion/endpoints/extra_link_endpoint.py) returns the
     * flat { linkName: url | null } map directly, with no wrapper — the spec doesn't match
     * the actual implementation here. Reading response.extra_links off that flat map was
     * always undefined, crashing the Details tab's Object.entries call downstream.
     */
    async getTaskInstanceLinks(Env: EnvVariables, dagId: string, dagRunId: string, taskId: string, mapIndex = -1): Promise<AirflowTaskInstanceLinks> {
        const query = mapIndex >= 0 ? buildQuery({ map_index: mapIndex }) : '';
        return get<AirflowTaskInstanceLinks>(
            `${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances/${encodeURIComponent(taskId)}/links${query}`
        );
    },

    /*
     * Updates a task instance's state (mark success/failed/skipped)
     */
    async patchTaskInstance(
        Env: EnvVariables,
        dagId: string,
        dagRunId: string,
        taskId: string,
        body: { new_state: 'success' | 'failed' | 'skipped'; map_index?: number }
    ): Promise<AirflowTaskInstance> {
        return patch(
            `${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances/${encodeURIComponent(taskId)}`,
            body
        );
    },

    /*
     * Deletes a task instance
     */
    async deleteTaskInstance(Env: EnvVariables, dagId: string, dagRunId: string, taskId: string): Promise<void> {
        return del(
            `${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances/${encodeURIComponent(taskId)}`
        );
    },

    /*
     * Lists XCom entries for a task instance
     */
    async listXcomEntries(
        Env: EnvVariables,
        dagId: string,
        dagRunId: string,
        taskId: string,
        params: { limit?: number; offset?: number; mapIndex?: number } = {}
    ): Promise<AirflowXcomEntryList> {
        // map_index is NOT a query param on this endpoint in 2.6.0 (confirmed against the
        // spec — only limit/offset are defined), so it's never sent on the wire. It's also
        // NOT a field on the response at all — confirmed straight from Airflow's own
        // xcom_schema.py (XComCollectionItemSchema only has key/timestamp/execution_date/
        // task_id/dag_id). A previous version of this method filtered client-side on
        // entry.map_index === params.mapIndex, which compared undefined to a number and was
        // always false — every real entry got silently discarded, showing "0 XCom entries"
        // even when the task genuinely had values. There's nothing to filter on, so don't.
        const query = buildQuery({ limit: params.limit ?? 50, offset: params.offset ?? 0 });
        return get<AirflowXcomEntryList>(
            `${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances/${encodeURIComponent(taskId)}/xcomEntries${query}`
        );
    },

    /*
     * Creates a new XCom entry for a task instance
     */
    async createXcomEntry(
        Env: EnvVariables,
        dagId: string,
        dagRunId: string,
        taskId: string,
        body: { key: string; value: unknown; map_index?: number }
    ): Promise<AirflowXcomEntry> {
        return post(
            `${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances/${encodeURIComponent(taskId)}/xcomEntries`,
            { map_index: -1, ...body }
        );
    },

    /*
     * Updates an existing XCom entry's value
     */
    async updateXcomEntry(
        Env: EnvVariables,
        dagId: string,
        dagRunId: string,
        taskId: string,
        xcomKey: string,
        body: { value: unknown; map_index?: number }
    ): Promise<AirflowXcomEntry> {
        return patch(
            `${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances/${encodeURIComponent(taskId)}/xcomEntries/${encodeURIComponent(xcomKey)}`,
            { map_index: -1, ...body }
        );
    },

    /*
     * Deletes an XCom entry
     */
    async deleteXcomEntry(Env: EnvVariables, dagId: string, dagRunId: string, taskId: string, xcomKey: string, mapIndex = -1): Promise<void> {
        const query = buildQuery({ map_index: mapIndex });
        return del(
            `${readBase(Env)}/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(dagRunId)}/taskInstances/${encodeURIComponent(taskId)}/xcomEntries/${encodeURIComponent(xcomKey)}${query}`
        );
    },

    /*
     * Lists dataset events tied to a source DAG/task/run (used by the Task Events tab) —
     * called "assets" in later Airflow versions, but "datasets" is the real term in 2.6.0.
     * The response wraps its array under "dataset_events" (confirmed from the
     * DatasetEventCollection schema), not "asset_events" — reading response.asset_events
     * was always undefined, crashing the Task Events tab's DataTable downstream. Remapped
     * to our AirflowAssetEventList shape here so the rest of the app is unaffected.
     */
    async listAssetEvents(
        Env: EnvVariables,
        params: { sourceDagId: string; sourceTaskId: string; sourceRunId: string; sourceMapIndex?: number; limit?: number; offset?: number }
    ): Promise<AirflowAssetEventList> {
        const query = buildQuery({
            limit: params.limit ?? 50,
            offset: params.offset ?? 0,
            order_by: '-timestamp',
            source_dag_id: params.sourceDagId,
            source_task_id: params.sourceTaskId,
            source_run_id: params.sourceRunId,
            source_map_index: params.sourceMapIndex !== undefined && params.sourceMapIndex >= 0 ? params.sourceMapIndex : undefined,
        });
        const response = await get<{ dataset_events: AirflowAssetEventList['asset_events']; total_entries: number }>(`${readBase(Env)}/datasets/events${query}`);
        return { asset_events: response.dataset_events ?? [], total_entries: response.total_entries ?? 0 };
    },
};
